import Axios from 'axios'
import type {
  TChatDto,
  TChatSummaryDto,
  TChatStreamDoneEvent,
  TRateChatMessageBody,
  TRateChatMessageResponse,
  TThinkingStepDto
} from '@homepal/api-contract'

import {
  getInternalWithRedirect,
  getWithRedirect,
  postInternalWithRedirect,
  postWithRedirect
} from 'utils/axios'
import { parseAuthenticationHeader } from 'helpers/Functions'
import { signAwsRequest } from 'utils/awsSigV4'

import {
  TChatHistoryBody,
  TChatStreamBody,
  TChatStreamCallbacks
} from 'types/GlobalChat'

const IS_DEV = process.env.NODE_ENV === 'development'

const BASE_URL = IS_DEV
  ? 'http://localhost:3990/'
  : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

type Credentials = 'include' | 'omit'

export async function getIntercomJwt() {
  return getInternalWithRedirect<{ jwt: string }>(`${BASE_URL}v1/intercom`)
}

// Rates a single assistant reply. Write-once — the backend rejects a second
// rating. The response is small enough to never be redirected to S3.
export async function rateMessage(
  chatId: string,
  messageId: string,
  body: TRateChatMessageBody
) {
  return Axios.post<TRateChatMessageResponse>(
    `${BASE_URL}v1/chats/${chatId}/messages/${messageId}/feedback`,
    { data: body },
    parseAuthenticationHeader()
  )
}

// ─── History ─────────────────────────────────────────────────────────────────

// The signed-in user's own chats started from one source and answering from
// the same view, newest first.
export async function getChats(body: TChatHistoryBody) {
  return postWithRedirect<TChatSummaryDto[]>(`${BASE_URL}v1/chats/history`, {
    data: body
  })
}

// One chat with its full message history, for replaying it in the panel.
export async function getChat(chatId: string) {
  return getWithRedirect<TChatDto>(`${BASE_URL}v1/chats/${chatId}`)
}

// ─── Streaming chat (POST /v1/chats/stream, Server-Sent Events) ──────────────

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isStartPayload(
  value: unknown
): value is { chat_id: string; title: string } {
  return (
    isObject(value) &&
    typeof value.chat_id === 'string' &&
    typeof value.title === 'string'
  )
}

function isToolPayload(value: unknown): value is TThinkingStepDto {
  return (
    isObject(value) && typeof value.tool === 'string' && isObject(value.input)
  )
}

function isDeltaPayload(value: unknown): value is { delta: string } {
  return isObject(value) && typeof value.delta === 'string'
}

function isErrorPayload(value: unknown): value is { message: string } {
  return isObject(value) && typeof value.message === 'string'
}

function isDonePayload(value: unknown): value is TChatStreamDoneEvent {
  return (
    isObject(value) &&
    typeof value.chat_id === 'string' &&
    typeof value.message_id === 'string' &&
    typeof value.reply === 'string'
  )
}

function parseFrame(frame: string): { event: string; data: string } | null {
  const lines = frame.split('\n')
  let event = 'message'
  let data = ''

  for (const line of lines) {
    if (line.startsWith('event: ')) event = line.slice(7).trim()
    else if (line.startsWith('data: ')) data += line.slice(6)
  }

  if (!data) return null

  return { event, data }
}

function dispatchFrame(
  event: string,
  parsed: unknown,
  callbacks: TChatStreamCallbacks
) {
  switch (event) {
  case 'start':
    if (isStartPayload(parsed)) callbacks.onStart(parsed)
    break
  case 'tool_start':
    if (isToolPayload(parsed)) callbacks.onToolStart(parsed)
    break
  case 'tool_end':
    if (isToolPayload(parsed)) callbacks.onToolEnd(parsed)
    break
  case 'text':
    if (isDeltaPayload(parsed)) callbacks.onText(parsed.delta)
    break
  case 'reasoning':
    if (isDeltaPayload(parsed)) callbacks.onReasoning(parsed.delta)
    break
  case 'done':
    if (isDonePayload(parsed)) callbacks.onDone(parsed)
    break
  case 'error':
    if (isErrorPayload(parsed)) callbacks.onError(parsed.message)
    break
  default:
    break
  }
}

type StreamCredentials = {
  accessKeyId: string
  secretAccessKey: string
  sessionToken: string
  expiration?: string
}

type StreamAuth = {
  token: string
  url: string
  region: string
  credentials: StreamCredentials
  // Epoch ms when the token or the credentials expire, whichever is first.
  expiresAt: number
}

// Fallback lifetime when the API does not say when the token expires.
const DEFAULT_AUTH_TTL_MS = 2 * 60 * 1000

// Auth is refreshed this long before it expires.
const AUTH_REFRESH_MARGIN_MS = 60 * 1000

function parseExpiry(value: unknown, fallback: number): number {
  const parsed = typeof value === 'string' ? Date.parse(value) : NaN

  return Number.isNaN(parsed) ? fallback : parsed
}

// Gets the streaming Function URL plus short-lived AWS credentials and the
// chat-context token from the cookie-authed API. The Function URL is AuthType
// AWS_IAM (the account blocks unauthenticated Function URLs), so the browser
// SigV4-signs the request with these credentials; the context token rides in a
// custom header since SigV4 owns the Authorization header.
export async function getStreamAuth(): Promise<StreamAuth> {
  const response = await postInternalWithRedirect<{
    token: string
    url: string
    token_expires_at?: string
    region?: string
    credentials?: StreamCredentials
  }>(`${BASE_URL}v1/chats/stream-token`)

  const { token, url, token_expires_at, region, credentials } = response.data

  if (
    typeof token !== 'string' ||
    typeof url !== 'string' ||
    !credentials?.accessKeyId
  ) {
    throw new Error('Malformed token response')
  }

  const fallback = Date.now() + DEFAULT_AUTH_TTL_MS

  return {
    token,
    url,
    region: region ?? 'eu-north-1',
    credentials,
    expiresAt: Math.min(
      parseExpiry(token_expires_at, fallback),
      parseExpiry(credentials.expiration, fallback)
    )
  }
}

let cachedAuth: Promise<StreamAuth> | null = null

// The cached auth, refreshed when missing, failed or about to expire.
async function loadStreamAuth(): Promise<StreamAuth> {
  if (cachedAuth) {
    const current = await cachedAuth.catch(() => null)

    if (current && current.expiresAt - Date.now() > AUTH_REFRESH_MARGIN_MS) {
      return current
    }
  }

  const fresh = getStreamAuth()

  cachedAuth = fresh
  fresh.catch(() => {
    if (cachedAuth === fresh) cachedAuth = null
  })

  return fresh
}

// Warms the auth cache so the first send skips the token round trip.
export function prefetchStreamAuth(): void {
  if (IS_DEV) return

  loadStreamAuth().catch(() => undefined)
}

// Opens the streaming response. Locally we hit the Express dev server directly
// with cookies; in production we SigV4-sign the off-domain AWS_IAM Function URL
// with credentials minted by the API.
async function openStream(
  body: TChatStreamBody,
  signal?: AbortSignal
): Promise<Response> {
  // The exact bytes sent must match what we hash for the signature.
  const payload = JSON.stringify({ data: body })

  if (IS_DEV) {
    return fetch(`${BASE_URL}v1/chats/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      body: payload,
      credentials: 'include' as Credentials,
      signal
    })
  }

  const { token, url, region, credentials } = await loadStreamAuth()

  const signed = await signAwsRequest({
    url,
    method: 'POST',
    body: payload,
    region,
    service: 'lambda',
    credentials
  })

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'x-chat-stream-token': token,
      ...signed
    },
    body: payload,
    signal
  })
}

/**
 * Posts a message and consumes the SSE stream from the backend.
 * In production the stream runs on a Lambda Function URL authenticated with
 * SigV4-signed short-lived credentials; locally it streams from Express.
 */
export async function streamChat(
  body: TChatStreamBody,
  callbacks: TChatStreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  let response: Response

  try {
    response = await openStream(body, signal)
  } catch (e) {
    callbacks.onError(e instanceof Error ? e.message : String(e))

    return
  }

  if (!response.ok || !response.body) {
    // The only conflict this endpoint raises is a read-only chat.
    if (response.status === 409) {
      callbacks.onReadOnly()

      return
    }

    callbacks.onError(`HTTP ${response.status}`)

    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()

    if (done) break

    buffer += decoder.decode(value, { stream: true })

    // SSE frames are separated by a blank line.
    let separatorIdx = buffer.indexOf('\n\n')

    while (separatorIdx !== -1) {
      const frame = buffer.slice(0, separatorIdx)

      buffer = buffer.slice(separatorIdx + 2)

      const parsedFrame = parseFrame(frame)

      if (parsedFrame) {
        try {
          const parsed: unknown = JSON.parse(parsedFrame.data)

          dispatchFrame(parsedFrame.event, parsed, callbacks)
        } catch {
          // Ignore frames with malformed JSON payloads.
        }
      }

      separatorIdx = buffer.indexOf('\n\n')
    }
  }
}
